import { format } from 'date-fns';
import React from 'react';
import { Box } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  PickersDay,
  type PickersDayProps,
} from '@mui/x-date-pickers/PickersDay';

export type MarkedCalendarDay = {
  key: string;
  color: string;
};

export type MarkedDateCalendarProps = {
  markedDays: readonly MarkedCalendarDay[];
  onMarkedDayHover?: (anchorEl: HTMLElement, day: Date) => void;
  onMarkedDayLeave?: () => void;
};

const MarkedDateCalendar: React.FC<MarkedDateCalendarProps> = ({
  markedDays,
  onMarkedDayHover,
  onMarkedDayLeave,
}) => {
  const markedDayColors = new Map(
    markedDays.map((day) => [day.key, day.color])
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateCalendar
        readOnly
        slots={{ day: MarkedDay as typeof PickersDay }}
        slotProps={{
          day: {
            markedDayColors,
            onMarkedDayHover,
            onMarkedDayLeave,
          } as never,
        }}
        sx={{
          width: '100%',
          '& .MuiPickersCalendarHeader-root': {
            px: 0,
            mt: 0,
          },
          '& .MuiDayCalendar-header, & .MuiDayCalendar-weekContainer': {
            justifyContent: 'space-between',
          },
        }}
      />
    </LocalizationProvider>
  );
};

type MarkedDayProps = PickersDayProps & {
  markedDayColors: ReadonlyMap<string, string>;
  onMarkedDayHover?: MarkedDateCalendarProps['onMarkedDayHover'];
  onMarkedDayLeave?: MarkedDateCalendarProps['onMarkedDayLeave'];
};

function MarkedDay(props: MarkedDayProps) {
  const {
    markedDayColors,
    onMarkedDayHover,
    onMarkedDayLeave,
    day,
    outsideCurrentMonth,
    ...other
  } = props;
  const tone = !outsideCurrentMonth
    ? markedDayColors.get(format(day, 'yyyy-MM-dd'))
    : undefined;

  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        mx: '2px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onMouseEnter={(event) => {
        if (tone) {
          onMarkedDayHover?.(event.currentTarget, day);
        }
      }}
      onMouseLeave={() => {
        if (tone) {
          onMarkedDayLeave?.();
        }
      }}
    >
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        disableMargin
        sx={{
          width: 30,
          height: 30,
          cursor: tone ? 'pointer' : 'default',
          '&:hover, &:focus': {
            bgcolor: 'transparent',
          },
          '&.MuiPickersDay-today': {
            fontWeight: 600,
            border: 'none',
            boxShadow: (theme) => `0 0 0 2px ${theme.palette.text.primary}`,
          },
          ...(tone && {
            fontWeight: 600,
            bgcolor: `${tone}.main`,
            color: `${tone}.contrastText`,
            '&:hover, &:focus, &.Mui-selected': {
              bgcolor: `${tone}.main`,
              color: `${tone}.contrastText`,
            },
          }),
        }}
      />
    </Box>
  );
}

export default MarkedDateCalendar;
